// DESIGN_AUDIT Phase 11C: hardcoded colors replaced with CSS vars
import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Cloud, Sun, CloudRain, CloudSnow, Wind, Droplets,
  Package, Users, ShoppingCart, CheckSquare,
  Calendar, AlertTriangle, Activity,
  Clock, Star, MapPin, RefreshCw, Key,
  ChevronDown, ChevronUp, X, Paperclip, Pin, Pencil, Trash2, Megaphone, Settings,
} from 'lucide-react';
import { api } from '../api/client.ts';
import { DashboardKpis, ActivityLog, Facility } from '../types/index.ts';
import { formatDistanceToNow } from 'date-fns';
import { useFacility } from '../contexts/FacilityContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTheme } from '../contexts/ThemeContext.tsx';
import RichTextEditor, { mdToHtml } from '../components/RichTextEditor.tsx';
import PageFocusToggle from '../components/PageFocusToggle.tsx';
import { spinIconOut } from '../utils/iconAnimations.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WeatherCurrent {
  temperature: number;
  weathercode: number;
  windspeed: number;
  time: string;
}

interface WeatherDaily {
  time: string[];
  weathercode: number[];
  temperature_2m_max: number[];
  temperature_2m_min: number[];
  precipitation_probability_max?: number[];
}

interface WeatherCurrentExtra {
  relative_humidity_2m?: number;
  apparent_temperature?: number;
  cloud_cover?: number;
}

interface WeatherResponse {
  current_weather: WeatherCurrent;
  current?: WeatherCurrentExtra;
  daily: WeatherDaily;
}

interface ScheduleItem {
  id: number;
  title: string;
  start_date: string;
  end_date?: string;
  category?: string;
  status?: string;
}

// ─── Dashboard card customization (Phase 3, Pro+Enterprise) ───────────────────
// Card ids used both by the settings-cog checklist and by the conditional wraps around each
// section below. Deliberately excludes the KPI grid and the low-balance banner — those stay
// locked/always-on, only the section-level cards are toggleable.
// Workboard/Quick Actions removed 2026-10-05 (see CLAUDE_RULES.md Dashboard LOCKED section) --
// a user with either id still sitting in their saved hiddenCards prefs just has an orphaned,
// harmless entry (the showX consts/render blocks that read it are gone too), not an error.
interface DashboardCardDef { id: string; label: string; }
const DASHBOARD_CARDS: DashboardCardDef[] = [
  { id: 'scheduled-maintenance', label: 'Scheduled Maintenance' },
  { id: 'weather', label: 'Weather' },
  { id: 'alerts-reminders', label: 'Alerts & Reminders' },
  { id: 'recent-activity', label: 'Recent Activity' },
  { id: 'bulletin-board', label: 'Bulletin Board' },
];

// ─── Weather helpers ──────────────────────────────────────────────────────────

function getWeatherIcon(code: number, size = 22): React.ReactNode {
  if (code <= 1) return <Sun size={size} color="#f59e0b" strokeWidth={1.5} />;
  if (code <= 3) return <Cloud size={size} color="#94a3b8" strokeWidth={1.5} />;
  if (code <= 67) return <CloudRain size={size} color="#60a5fa" strokeWidth={1.5} />;
  if (code <= 86) return <CloudSnow size={size} color="#a5b4fc" strokeWidth={1.5} />;
  return <Wind size={size} color="#94a3b8" strokeWidth={1.5} />;
}

function getWeatherLabel(code: number): string {
  if (code === 0) return 'Clear';
  if (code === 1) return 'Mainly clear';
  if (code === 2) return 'Partly cloudy';
  if (code === 3) return 'Overcast';
  if (code <= 9) return 'Fog';
  if (code <= 19) return 'Drizzle';
  if (code <= 29) return 'Rain / sleet';
  if (code <= 39) return 'Fog / ice';
  if (code <= 49) return 'Fog';
  if (code <= 59) return 'Drizzle';
  if (code <= 67) return 'Rain';
  if (code <= 77) return 'Snow';
  if (code <= 82) return 'Rain showers';
  if (code <= 86) return 'Snow showers';
  return 'Thunderstorm';
}

function getWeatherEmoji(code: number): string {
  if (code === 0) return '☀️';
  if (code <= 1) return '🌤️';
  if (code <= 2) return '⛅';
  if (code <= 3) return '☁️';
  if (code <= 9) return '🌫️';
  if (code <= 67) return '🌧️';
  if (code <= 77) return '❄️';
  if (code <= 82) return '🌦️';
  if (code <= 86) return '🌨️';
  return '⛈️';
}

const SNOW_CODES = new Set([71, 72, 73, 74, 75, 76, 77, 85, 86]);

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ─── Activity badge colours ────────────────────────────────────────────────────

const ACTIVITY_TYPE_COLORS: Record<string, string> = {
  // Legacy CamelCase keys
  VendorAdded: 'badge-green',
  ItemAdded: 'badge-blue',
  ItemDeleted: 'badge-red',
  VendorDeleted: 'badge-red',
  OrderCreated: 'badge-amber',
  TaskCompleted: 'badge-purple',
  // Current action_entityType format from logActivity
  add_item: 'badge-blue',
  delete_item: 'badge-red',
  edit_item: 'badge-indigo',
  add_vendor: 'badge-green',
  delete_vendor: 'badge-red',
  approve_import: 'badge-green',
  reject_import: 'badge-red',
  created_order: 'badge-amber',
  complete_order: 'badge-purple',
  add_task: 'badge-indigo',
  complete_task: 'badge-purple',
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  accentColor: string;
  subtitle?: string;
  onClick?: () => void;
}

function KpiCard({ label, value, icon, accentColor, subtitle, onClick }: KpiCardProps) {
  return (
    <div
      className="card dash-kpi-card" // DS: #1 KpiCard
      onClick={onClick}
      // Same lift + border-highlight treatment as Compliance PM's KPI cards / Vendors.tsx's
      // .vendor-card:hover. Only right/bottom/left border-color is touched (not the shorthand)
      // so the card's own colored top accent (set via borderTop below) survives the hover —
      // touching the shorthand would blow that away, same trap documented in Tasks.tsx.
      // The icon's wiggle-in is driven by .dash-kpi-card:hover .btn-icon (defined in the <style>
      // tag below the KPI grid) rather than combo-jello-spin — matching Compliance PM's own
      // .kpi-hover-tip pattern exactly: the whole card is the hover surface, not the small icon
      // square, and spin-out fires from the card's own onMouseLeave (below), not the icon's.
      style={{ display: 'flex', alignItems: 'center', borderTop: `3px solid ${accentColor ?? 'var(--accent-primary)'}`, transition: 'all 0.2s ease', ...(onClick ? { cursor: 'pointer' } : {}) }}
      onMouseEnter={onClick ? (e) => {
        const isLight = document.documentElement.getAttribute('data-theme') === 'light';
        const c = isLight ? 'rgba(59,130,246,0.5)' : 'rgba(59,130,246,0.4)';
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.borderRightColor = c;
        e.currentTarget.style.borderBottomColor = c;
        e.currentTarget.style.borderLeftColor = c;
        e.currentTarget.style.boxShadow = isLight ? '0 8px 24px rgba(0,0,0,0.12)' : '0 8px 24px rgba(0,0,0,0.3)';
      } : undefined}
      onMouseLeave={onClick ? (e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.borderRightColor = 'var(--border-default)';
        e.currentTarget.style.borderBottomColor = 'var(--border-default)';
        e.currentTarget.style.borderLeftColor = 'var(--border-default)';
        e.currentTarget.style.boxShadow = '';
        spinIconOut(e);
      } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}> {/* DS: #1 KpiCard inner layout */}
        <div style={{ width: 36, height: 36, borderRadius: 8, background: accentColor ? `${accentColor}1f` : 'rgba(59,130,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}> {/* DS: #1 KpiCard icon container */}
          {icon}
        </div>
        <div>
          <div className="kpi-label">{label}</div> {/* DS: #1 KpiCard label */}
          <div className="kpi-value">{value}</div> {/* DS: #1 KpiCard value */}
          {subtitle && (
            <div style={{ fontSize: '0.75rem', color: accentColor ?? 'var(--text-muted)' }}>{subtitle}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Weather Panel ────────────────────────────────────────────────────────────

interface GeocodeResult {
  latitude: number;
  longitude: number;
  name: string;
}

function WeatherPanel({ isMobile }: { isMobile: boolean }) {
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const [isCelsius, setIsCelsius] = useState<boolean>(
    () => localStorage.getItem('weatherUnit') !== 'fahrenheit'
  );

  // Fetch facility data to get zip code
  const { data: facilityRes, isLoading: facilityLoading } = useQuery({
    queryKey: ['facility-detail', facilityId],
    queryFn: () => api.get<Facility>(`/facilities/${facilityId}`),
    enabled: facilityId > 0,
    staleTime: 5 * 60_000,
  });
  const facilityInfo = (facilityRes as unknown as { data: Facility })?.data;
  const zip = facilityInfo?.zip ?? null;

  // Geocode zip to lat/lon via Open-Meteo geocoding API
  const { data: geocodeResult, isLoading: geocodeLoading } = useQuery<GeocodeResult | null>({
    queryKey: ['geocode-zip', zip],
    queryFn: async () => {
      if (!zip) return null;
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(zip)}&count=1&language=en&format=json`
      );
      const data = await res.json() as { results?: GeocodeResult[] };
      return data.results?.[0] ?? null;
    },
    enabled: !!zip,
    staleTime: 24 * 60 * 60_000,
  });

  // Fall back to facility_settings weather_location / lat/lon if no zip or geocode
  const { data: settingsRes } = useQuery({
    queryKey: ['weather-settings', facilityId],
    queryFn: () =>
      api.get<Record<string, string>>('/settings/facility-settings', {
        keys: 'weather_lat,weather_lon,weather_location',
      }),
    staleTime: 5 * 60_000,
  });

  const raw = (settingsRes as unknown as { data: Record<string, string> })?.data ?? {};
  const settingsLocation = raw['weather_location'] ?? null;

  // Priority 2: geocode the appearance/facility_settings weather_location if no facility zip
  const { data: locationGeocode } = useQuery<GeocodeResult | null>({
    queryKey: ['geocode-location', settingsLocation],
    queryFn: async () => {
      if (!settingsLocation) return null;
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(settingsLocation)}&count=1&language=en&format=json`
      );
      const data = await res.json() as { results?: GeocodeResult[] };
      return data.results?.[0] ?? null;
    },
    enabled: !zip && !!settingsLocation,
    staleTime: 24 * 60 * 60_000,
  });

  // Priority: facility zip geocode > settings location geocode > raw lat/lon > NYC defaults
  const lat = geocodeResult?.latitude ?? locationGeocode?.latitude ?? (parseFloat(raw['weather_lat'] ?? '') || 40.7128);
  const lon = geocodeResult?.longitude ?? locationGeocode?.longitude ?? (parseFloat(raw['weather_lon'] ?? '') || -74.006);
  const locationLabel = zip ?? settingsLocation ?? null;
  const switchingFacility = facilityLoading || (!!zip && geocodeLoading) || (!zip && !!settingsLocation && !locationGeocode);

  const { data: weatherData, isLoading: weatherLoading, dataUpdatedAt } = useQuery<WeatherResponse>({
    queryKey: ['open-meteo', lat, lon],
    queryFn: async () => {
      const url =
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
        `&current_weather=true&current=relative_humidity_2m,apparent_temperature,cloud_cover` +
        `&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
        `&timezone=auto&forecast_days=7`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Weather fetch failed');
      return res.json() as Promise<WeatherResponse>;
    },
    staleTime: 60_000 * 30,
    refetchInterval: 60_000 * 30,
  });

  // Minute ticker: counts minutes since last weather fetch
  const [minsSinceUpdate, setMinsSinceUpdate] = useState(0);
  useEffect(() => {
    if (!dataUpdatedAt) return;
    const tick = () => setMinsSinceUpdate(Math.floor((Date.now() - dataUpdatedAt) / 60_000));
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [dataUpdatedAt]);

  const [isExpanded, setIsExpanded] = useState<boolean>(
    () => localStorage.getItem('weather_card_expanded') !== 'false'
  );

  function toggleExpanded() {
    const next = !isExpanded;
    setIsExpanded(next);
    localStorage.setItem('weather_card_expanded', String(next));
  }

  const isLoading = weatherLoading && !weatherData;

  const current = weatherData?.current_weather;
  const extra = weatherData?.current;
  const daily = weatherData?.daily;

  const isSnowing =
    (current ? SNOW_CODES.has(current.weathercode) : false) ||
    (daily ? daily.weathercode.slice(0, 3).some(code => SNOW_CODES.has(code)) : false);

  // Compute which specific days have snow for the alert message
  const snowDayLabels: string[] = (() => {
    if (!daily) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const labels: string[] = [];
    daily.time.slice(0, 3).forEach((dateStr, i) => {
      const isCurrentSnow = i === 0 && current && SNOW_CODES.has(current.weathercode);
      if (!SNOW_CODES.has(daily.weathercode[i]) && !isCurrentSnow) return;
      const d = new Date(dateStr + 'T00:00:00');
      const diffDays = Math.round((d.getTime() - today.getTime()) / 86_400_000);
      if (diffDays === 0) labels.push('Today');
      else if (diffDays === 1) labels.push('Tomorrow');
      else labels.push(d.toLocaleDateString('en-US', { weekday: 'long' }));
    });
    return labels;
  })();

  function toDisplay(celsius: number): number {
    return isCelsius ? Math.round(celsius) : Math.round(celsius * 9 / 5 + 32);
  }

  function toggleUnit() {
    const newVal = !isCelsius;
    setIsCelsius(newVal);
    localStorage.setItem('weatherUnit', newVal ? 'celsius' : 'fahrenheit');
  }

  // Header "place" text -- prefer the geocoded city/place name (from the facility zip, or from
  // the settings-location fallback geocode) matching the mockup's separate zip+place split;
  // before that resolves (or with no zip at all), fall back to the raw locationLabel/settings
  // string, then finally the literal 'Weather' the header always showed when nothing is set.
  const placeName = geocodeResult?.name ?? locationGeocode?.name ?? (!zip ? locationLabel : null) ?? 'Weather';

  // Mockup Option A's ".unit-toggle" pill -- rounded pill container, 3px padding, the active
  // unit gets a solid accent fill + white text, the inactive one stays transparent/muted.
  const unitToggle = (
    <div style={{
      display: 'inline-flex', background: 'rgba(255,255,255,0.05)',
      border: '1px solid var(--border-default)', borderRadius: 99, padding: 3,
    }}>
      <button onClick={toggleUnit} style={{
        border: 'none', background: !isCelsius ? 'var(--accent-primary)' : 'transparent',
        color: !isCelsius ? '#fff' : 'var(--text-secondary)', fontWeight: 700, fontSize: '0.7rem',
        padding: '4px 11px', borderRadius: 99, cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace",
      }}>°F</button>
      <button onClick={toggleUnit} style={{
        border: 'none', background: isCelsius ? 'var(--accent-primary)' : 'transparent',
        color: isCelsius ? '#fff' : 'var(--text-secondary)', fontWeight: 700, fontSize: '0.7rem',
        padding: '4px 11px', borderRadius: 99, cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace",
      }}>°C</button>
    </div>
  );

  // Mockup Option A's ".collapse-btn" pill -- reused for both directions (expand/collapse), only
  // the icon/label and onClick differ.
  function ForecastToggleButton({ expanded }: { expanded: boolean }) {
    return (
      <button className="combo1" onClick={toggleExpanded} onMouseLeave={spinIconOut} style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '7px 15px', borderRadius: 99,
        border: '1px solid rgba(59,130,246,0.35)', background: 'rgba(59,130,246,0.10)',
        color: '#93c5fd', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
      }}>
        {expanded ? <ChevronUp size={14} className="btn-icon" /> : <ChevronDown size={14} className="btn-icon" />}
        {expanded ? 'Collapse' : '7-day forecast'}
      </button>
    );
  }

  // Hero (icon + temp + condition + humidity/wind metrics) -- mockup's ".hero-now", shared by the
  // collapsed view (alone) and the expanded view (alongside the week strip). Same getWeatherEmoji
  // data/icon source the app already used -- only the surrounding layout changed.
  function HeroNow({ withDivider }: { withDivider: boolean }) {
    if (!current) return null;
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        paddingRight: withDivider ? 26 : 0,
        borderRight: withDivider ? '1px solid var(--border-subtle)' : 'none',
      }}>
        <span style={{ fontSize: 64, lineHeight: 1, width: 82, textAlign: 'center', flexShrink: 0 }}>
          {getWeatherEmoji(current.weathercode)}
        </span>
        <div>
          <div style={{ fontSize: '3.4rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em', color: 'var(--text-primary)' }}>
            {toDisplay(current.temperature)}<sup style={{ fontSize: '1.3rem', fontWeight: 600, verticalAlign: 'top', color: 'var(--text-secondary)' }}>°{isCelsius ? 'C' : 'F'}</sup>
          </div>
          <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', marginTop: 4 }}>
            {getWeatherLabel(current.weathercode)}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              <Droplets size={14} color="var(--text-muted)" />
              {extra?.relative_humidity_2m != null ? `${extra.relative_humidity_2m}%` : '—'}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              <Wind size={14} color="var(--text-muted)" />
              {Math.round(current.windspeed)} km/h
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minWidth: 0 }}>
      {/* Snow alert */}
      {isSnowing && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', borderRadius: 'var(--radius-md)',
          background: 'rgba(96, 165, 250, 0.15)',
          border: '1px solid rgba(96, 165, 250, 0.4)',
          marginBottom: 16,
        }}>
          <CloudSnow size={18} color="#60a5fa" />
          <span style={{ color: '#60a5fa', fontWeight: 600, fontSize: '0.9rem' }}>
            ❄️ Snow Alert — Snowy conditions forecast for {snowDayLabels.length > 0 ? snowDayLabels.join(', ') : 'upcoming days'}. Take necessary precautions.
          </span>
        </div>
      )}

      <div className="dash-glass-card" style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}> {/* DS: #6 WeatherWidget card wrapper */}
        {/* Facility-switch loading overlay */}
        {switchingFacility && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 2,
            background: 'rgba(var(--bg-surface-rgb, 255,255,255), 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(1px)',
          }}>
            <div className="loading-spinner" style={{ width: 20, height: 20 }} />
          </div>
        )}
        {/* Card header: pin icon + zip/place + °F/°C pill toggle -- mockup's .whead */}
        <div style={{ padding: '16px 22px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontWeight: 600, minWidth: 0 }}>
            <MapPin size={17} color="var(--accent-primary)" className="card-title-icon" style={{ flexShrink: 0 }} />
            {zip && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>{zip}</span>}
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {placeName}
            </span>
          </div>
          <div style={{ marginLeft: 'auto' }}>{unitToggle}</div>
        </div>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
            <div className="loading-spinner" style={{ width: 24, height: 24 }} />
          </div>
        ) : current ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '18px 22px 20px' }}>
            {!isExpanded ? (
              <>
                {/* "Centered + Strip" (mockups/weather-card-collapsed-currentday.html view-2/.i2) --
                    icon+temp+condition centered up top, a 4-column metric strip below with a
                    top-border separator. Replaces the previous collapsed layout (HeroNow alone),
                    which is still used unchanged by the expanded view below. */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                    <span style={{ fontSize: 56, lineHeight: 1, width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {getWeatherEmoji(current.weathercode)}
                    </span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '2.9rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em', color: 'var(--text-primary)' }}>
                        {toDisplay(current.temperature)}°
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                        {getWeatherLabel(current.weathercode)}
                        {daily && (
                          <>
                            {' · '}
                            <span style={{ fontWeight: 700 }}>H:{toDisplay(daily.temperature_2m_max[0])}°</span>{' '}
                            <span style={{ color: 'var(--text-muted)' }}>L:{toDisplay(daily.temperature_2m_min[0])}°</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, marginTop: 18, borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
                    {[
                      { label: 'Feels like', value: `${toDisplay(extra?.apparent_temperature ?? current.temperature)}°` },
                      { label: 'Humidity', value: extra?.relative_humidity_2m != null ? `${extra.relative_humidity_2m}%` : '—' },
                      { label: 'Wind', value: `${Math.round(current.windspeed)} km/h` },
                      { label: 'Precip', value: daily?.precipitation_probability_max?.[0] != null ? `${daily.precipitation_probability_max[0]}%` : '—' },
                    ].map((m, i) => (
                      <div key={i} style={{ borderRight: i < 3 ? '1px solid var(--border-subtle)' : 'none' }}>
                        <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', fontWeight: 700 }}>{m.label}</div>
                        <div style={{ fontSize: '1.05rem', fontWeight: 700, marginTop: 5 }}>{m.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginTop: 16 }}>
                  <ForecastToggleButton expanded={false} />
                </div>
              </>
            ) : (
              <>
                {/* Hero-grid: hero-now (left) + 7-day week strip (right) -- mockup's .hero-grid.
                    Mobile: stacked instead of side-by-side, strip scrolls horizontally in its own
                    bounded container so it can't force the shared dashboard row grid wide. */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr',
                  gap: isMobile ? 18 : 28,
                  alignItems: 'center',
                  minWidth: 0,
                }}>
                  <HeroNow withDivider={!isMobile} />
                  <div style={{
                    display: 'grid',
                    // minmax(0, 1fr), not bare 1fr -- bare 1fr tracks default to min-width:auto,
                    // so they refuse to shrink below each day cell's own content width. With the
                    // sidebar expanded (less room for this card), the 7 columns' combined natural
                    // width (~360px) exceeded the available track space, and instead of the columns
                    // compressing, the content overflowed this container and got clipped by the
                    // card -- the last day (or two) visually disappeared. minmax(0, 1fr) lets each
                    // column actually shrink to fit. Confirmed via real DOM measurement in both
                    // Chromium and Firefox: sidebar expanded -> strip container was 276px wide vs
                    // 360px of content (last day clipped); collapsed -> 354px (not clipped) --
                    // identical numbers in both browsers, so this is a plain CSS bug, not a
                    // Firefox-specific quirk.
                    gridTemplateColumns: isMobile ? undefined : 'repeat(7, minmax(0, 1fr))',
                    gridAutoFlow: isMobile ? 'column' : undefined,
                    gap: 4,
                    minWidth: 0,
                    overflowX: isMobile ? 'auto' : undefined,
                    WebkitOverflowScrolling: isMobile ? 'touch' : undefined,
                    touchAction: isMobile ? 'pan-x' : undefined,
                  }}>
                    {daily && daily.time.slice(0, 7).map((dateStr, i) => (
                      <div
                        key={i}
                        style={{
                          textAlign: 'center', padding: '10px 2px', borderRadius: 12,
                          background: i === 0 ? 'rgba(59,130,246,0.12)' : 'transparent',
                          transition: 'background 0.14s ease',
                          minWidth: isMobile ? 58 : undefined,
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => { if (i !== 0) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                        onMouseLeave={(e) => { if (i !== 0) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: i === 0 ? '#93c5fd' : 'var(--text-secondary)' }}>
                          {i === 0 ? 'Today' : new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div style={{ fontSize: 32, margin: '7px auto 6px', lineHeight: 1 }}>
                          {getWeatherEmoji(daily.weathercode[i])}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                          {toDisplay(daily.temperature_2m_max[i])}°
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {toDisplay(daily.temperature_2m_min[i])}°
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 16 }}>
                  <ForecastToggleButton expanded />
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{ padding: 32, color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center' }}>
            Weather data unavailable.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shared date formatter ────────────────────────────────────────────────────

function formatDueDate(d: string | null | undefined): string {
  if (!d) return 'No date set';
  const parts = String(d).slice(0, 10).split('-').map(Number);
  const date = parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : new Date(d);
  if (isNaN(date.getTime())) return 'No date set';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Scheduled Maintenance ─────────────────────────────────────────────────────

interface UpcomingTask {
  id: number;
  name: string;
  vendor?: string;
  frequency: string;
  status: 'due_soon' | 'overdue';
  nextOccurrenceDate: string;
}

function ScheduledMaintenancePanel() {
  const navigate = useNavigate();
  const { facilityId } = useFacility();

  const { data: upcomingData } = useQuery({
    queryKey: ['scheduled-tasks-upcoming', facilityId],
    queryFn: async () => {
      const res = await api.get<any>('/scheduled-tasks/upcoming');
      const body = (res as any)?.data ?? res;
      const arr = body?.data ?? body;
      return Array.isArray(arr) ? arr : [];
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
  const upcomingTasks: UpcomingTask[] = upcomingData ?? [];

  function formatDue(d: string | null | undefined): string {
    if (!d) return 'No date set';
    const date = new Date(d);
    if (isNaN(date.getTime())) return 'No date set';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {upcomingTasks.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', padding: '12px 16px' }}>
          No compliance tasks due soon.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent', maxHeight: 216 }}>
          {upcomingTasks.map((task) => (
            <div key={task.id} onClick={() => navigate('/tasks?tab=schedule&highlightTask=' + task.id)} onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', minHeight: 44 }}> {/* DS: #4 ScheduledMaintenance row item */}
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: task.status === 'overdue' ? 'var(--accent-danger)' : 'var(--accent-warning)', flexShrink: 0, display: 'inline-block' }} /> {/* DS: #4 ScheduledMaintenance status dot */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {task.name}
                </div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{task.frequency}</span>
              </div>
              <span className={task.status === 'overdue' ? 'badge badge-red' : 'badge badge-amber'} style={{ flexShrink: 0 }}>
                {task.status === 'overdue' ? 'Overdue' : 'Due Soon'}
              </span>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', flexShrink: 0 }}>
                {formatDue(task.nextOccurrenceDate)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const { facilityId } = useFacility();
  const { user } = useAuth();
  const qc = useQueryClient();
  const canCustomize = user?.orgPlanTier === 'pro' || user?.orgPlanTier === 'enterprise' || user?.role === 'admin';

  const [prefsPanelOpen, setPrefsPanelOpen] = useState(false);
  const prefsPanelRef = useRef<HTMLDivElement>(null);

  const { data: prefsRes } = useQuery({
    queryKey: ['dashboard-prefs'],
    queryFn: () => api.get<{ hiddenCards: string[] }>('/dashboard/prefs'),
    enabled: canCustomize,
    staleTime: 60_000,
  });
  const hiddenCards: string[] = (prefsRes as any)?.data?.hiddenCards ?? [];

  const savePrefsMutation = useMutation({
    mutationFn: (next: string[]) => api.put('/dashboard/prefs', { hiddenCards: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard-prefs'] }),
  });

  function toggleCard(cardId: string) {
    const next = hiddenCards.includes(cardId)
      ? hiddenCards.filter((c) => c !== cardId)
      : [...hiddenCards, cardId];
    savePrefsMutation.mutate(next);
  }

  useEffect(() => {
    if (!prefsPanelOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (prefsPanelRef.current && !prefsPanelRef.current.contains(e.target as Node)) {
        setPrefsPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [prefsPanelOpen]);

  const { data: keysStatsRes } = useQuery({
    queryKey: ['keys-stats-dashboard', facilityId],
    queryFn: () => api.get<{ total: number; out: number }>('/tasks/keys/stats'),
    refetchInterval: 60_000,
  });
  const { data: kpisRes, isLoading: kpisLoading } = useQuery({
    queryKey: ['dashboard-kpis', facilityId],
    queryFn: () => api.get<DashboardKpis>('/dashboard/kpis'),
    refetchInterval: 60_000,
  });

  const { data: balanceRes } = useQuery({
    queryKey: ['anthropic-balance', user?.role],
    queryFn: () => api.get('/dashboard/anthropic-balance-status'),
    staleTime: 300_000,
    enabled: user?.role === 'admin',
    retry: false,
  });
  const anthropicBalanceLow = (balanceRes as any)?.data?.isLow === true;

  const { data: activityRes, isLoading: activityLoading } = useQuery({
    queryKey: ['dashboard-activity', facilityId],
    queryFn: () => api.get<ActivityLog[]>('/activity', { limit: '20' }),
    refetchInterval: 60_000,
  });

  const { data: remindersRes } = useQuery({
    queryKey: ['dashboard-reminders', facilityId],
    queryFn: () =>
      api.get<{
        overdueTasks: Array<{ id: number; title: string; due_date: string; priority: string }>;
        dueSoonTasks: Array<{ id: number; title: string; due_date: string; priority: string }>;
        upcomingEvents: Array<{ id: number; title: string; start_date: string }>;
        lowStockItems: Array<{ id: number; name: string; quantity: number; reorder_point: number }>;
        scheduledProjects: Array<{ id: number; name: string; scheduled_date: string; status: string; workflow_status: string }>;
        overdueScheduledProjects: Array<{ id: number; name: string; scheduled_date: string; status: string; workflow_status: string }>;
      }>('/dashboard/reminders'),
  });

  const { data: scheduledTasksRes } = useQuery({
    queryKey: ['scheduled-tasks-upcoming', facilityId],
    queryFn: async () => {
      const res = await api.get<Array<{ id: number; name: string; nextOccurrenceDate: string; frequency: string; status: 'overdue' | 'due_soon' }>>('/scheduled-tasks/upcoming');
      return (res as any)?.data ?? [];
    },
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const kpis = kpisRes?.data;
  const activity = activityRes?.data ?? [];
  const reminders = remindersRes?.data;
  const upcomingScheduled = (scheduledTasksRes as any) ?? [];
  const today = new Date().toISOString().slice(0, 10);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Card visibility — always true when customization isn't available (Standard tier / no prefs
  // saved yet), so nothing hides for users who never had a way to hide it.
  const showScheduledMaintenance = !hiddenCards.includes('scheduled-maintenance');
  const showWeather = !hiddenCards.includes('weather');
  const showAlertsReminders = !hiddenCards.includes('alerts-reminders');
  const showRecentActivity = !hiddenCards.includes('recent-activity');
  const showBulletinBoard = !hiddenCards.includes('bulletin-board');

  return (
    <div>
      {anthropicBalanceLow && (
        <div style={{
          marginBottom: 16,
          padding: '10px 16px',
          borderRadius: 10,
          background: 'rgba(245,158,11,0.12)',
          border: '1px solid rgba(245,158,11,0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertTriangle size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
            <div>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#fcd34d' }}>
                Anthropic API credit balance is low
              </span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginLeft: 8 }}>
                AI features (vendor extract, contract analysis, bid analysis) are unavailable
              </span>
            </div>
          </div>
          <a
            href="https://console.anthropic.com/settings/billing"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: 12, fontWeight: 500, padding: '4px 12px',
              borderRadius: 6, whiteSpace: 'nowrap',
              border: '1px solid rgba(245,158,11,0.4)',
              background: 'rgba(245,158,11,0.15)',
              color: '#fcd34d', textDecoration: 'none',
            }}
          >
            Add Credits →
          </a>
        </div>
      )}

      {/* Scheduled Maintenance + Weather 50/50 top row — wrapped (not modified) so each side can
          be hidden independently per Phase 3's dashboard card customization. */}
      {(showScheduledMaintenance || showWeather) && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile || !(showScheduledMaintenance && showWeather) ? '1fr' : '1fr 1fr', gridAutoRows: '1fr', gap: 16, marginBottom: 24 }}>
          {showScheduledMaintenance && (
            <div className="dash-glass-card" style={{ padding: 0, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Calendar size={16} color="#06b6d4" className="card-title-icon" />
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>Scheduled Maintenance</h3>
              </div>
              <ScheduledMaintenancePanel />
            </div>
          )}
          {showWeather && <WeatherPanel isMobile={isMobile} />}
        </div>
      )}

      {/* KPI Grid */}
      {/* .dash-kpi-card:hover .btn-icon triggers the wiggle-in from anywhere on the card, not just
          the small icon square — same non-button-icon-trigger pattern as Tasks.tsx's
          .kpi-hover-tip:hover .btn-icon (see buttonAnimations.css). Dashboard.tsx doesn't import
          buttonAnimations.css directly (deliberate, see the .bb-hero-grid comment elsewhere in
          this file) — the iconWiggle keyframes are already loaded globally via Sidebar.tsx/
          TopBar.tsx, so this scoped <style> tag only needs to add the trigger rule itself. */}
      <style>{`
        .dash-kpi-card:hover .btn-icon { animation: iconWiggle 0.45s ease; }
        /* Card title icons (Alerts & Reminders/Recent Activity/Scheduled Maintenance) wiggle
           from anywhere on the card too, same whole-card-is-the-trigger pattern as the KPI
           cards above -- but scoped to .card-title-icon instead of .btn-icon so it doesn't
           also fire on unrelated buttons living inside the same .dash-glass-card. (Workboard/
           Quick Actions removed 2026-10-05 -- this rule is still shared by the remaining cards.) */
        .dash-glass-card:hover .card-title-icon { animation: iconWiggle 0.45s ease; }
        /* Shared Dashboard card shell -- exact shape the weather card already used (mockups/
           weather-card-redesign.html's .wcard), now the single source every non-KPI Dashboard
           card reuses instead of each one hand-copying the same background/border/blur/sheen.
           padding:20px preserved from global.css's .card (which this replaces on each card) so
           cards that relied on that default padding (Alerts & Reminders, Recent Activity) don't
           lose it -- cards with their own inline padding override it as normal. KPI cards
           intentionally keep using .card, not this class. */
        .dash-glass-card {
          position: relative;
          border-radius: 20px;
          background: rgba(255,255,255,0.045);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          border: 1px solid var(--border-subtle);
          padding: 20px;
          overflow: hidden;
        }
        .dash-glass-card::before {
          content: '';
          position: absolute;
          top: 0; left: 24px; right: 24px; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
          pointer-events: none;
        }
      `}</style>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(6, 1fr)', gap: isMobile ? '10px' : '16px', marginBottom: '24px', marginTop: 20 }}>
        <KpiCard
          label="Catalog Items"
          value={kpisLoading ? '—' : (kpis?.totalItems ?? 0)}
          icon={<Package size={18} color="#3b82f6" className="btn-icon" />}
          accentColor="#3b82f6"
          onClick={() => navigate('/inventory')}
        />
        <div
          className="card dash-kpi-card" // DS: #1 KpiCard Facility Keys
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', borderTop: '3px solid #f97316', transition: 'all 0.2s ease' }}
          onClick={() => navigate('/inventory?tab=facility-keys')}
          // Same lift + border-highlight + wiggle-in trigger as the KpiCard component above — see
          // its comment for the full explanation (this card predates KpiCard, hand-rolled).
          onMouseEnter={(e) => {
            const isLight = document.documentElement.getAttribute('data-theme') === 'light';
            const c = isLight ? 'rgba(59,130,246,0.5)' : 'rgba(59,130,246,0.4)';
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.borderRightColor = c;
            e.currentTarget.style.borderBottomColor = c;
            e.currentTarget.style.borderLeftColor = c;
            e.currentTarget.style.boxShadow = isLight ? '0 8px 24px rgba(0,0,0,0.12)' : '0 8px 24px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = '';
            e.currentTarget.style.borderRightColor = 'var(--border-default)';
            e.currentTarget.style.borderBottomColor = 'var(--border-default)';
            e.currentTarget.style.borderLeftColor = 'var(--border-default)';
            e.currentTarget.style.boxShadow = '';
            spinIconOut(e);
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}> {/* DS: #1 KpiCard inner layout */}
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(249,115,22,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}> {/* DS: #1 KpiCard icon container */}
              <Key size={18} color="#14b8a6" className="btn-icon" />
            </div>
            <div>
              <div className="kpi-label">Facility Keys</div> {/* DS: #1 KpiCard label */}
              <div className="kpi-value">{keysStatsRes?.data?.total ?? '—'}</div> {/* DS: #1 KpiCard value */}
              {keysStatsRes?.data && (
                <div style={{ fontSize: '0.75rem', color: '#f97316' }}>{keysStatsRes.data.out} checked out</div>
              )}
            </div>
          </div>
        </div>
        <KpiCard
          label="Active Orders"
          value={kpisLoading ? '—' : (kpis?.activeOrders ?? 0)}
          icon={<ShoppingCart size={18} color="#f97316" className="btn-icon" />}
          accentColor="#f59e0b"
          subtitle={kpis && (kpis.agingOrders ?? 0) > 0 ? `${kpis.agingOrders} aging >15d` : undefined}
          onClick={() => navigate('/orders')}
        />
        <KpiCard
          label="Pending Tasks"
          value={kpisLoading ? '—' : (kpis?.pendingTasks ?? 0)}
          icon={<CheckSquare size={18} color="#8b5cf6" className="btn-icon" />}
          accentColor="#8b5cf6"
          onClick={() => navigate('/tasks')}
        />
        <KpiCard
          label="Active Vendors"
          value={kpisLoading ? '—' : (kpis?.totalVendors ?? 0)}
          icon={<Users size={18} color="#10b981" className="btn-icon" />}
          accentColor="#10b981"
          subtitle={kpis && (kpis.contractsDueSoon ?? 0) > 0 ? `${kpis.contractsDueSoon} contracts due` : undefined}
          onClick={() => navigate('/vendors')}
        />
        <KpiCard
          label="Upcoming Events"
          value={kpisLoading ? '—' : (kpis?.upcomingEvents ?? 0)}
          icon={<Calendar size={18} color="#06b6d4" className="btn-icon" />}
          accentColor="#06b6d4"
          subtitle="Next 30 days"
          onClick={() => navigate('/schedule')}
        />
      </div>

      {/* Main content grid */}
      <div className="dashboard-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>

        {/* Alerts & Reminders */}
        {showAlertsReminders && (
        <div className="dash-glass-card" style={{ minWidth: 0 }}> {/* DS: #2 AlertsReminders card wrapper */}
          <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}> {/* DS: #2 AlertsReminders section title */}
            <AlertTriangle size={16} color="#f59e0b" className="card-title-icon" /> Alerts & Reminders
          </h3>

          <div style={{
            maxHeight: 260,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255,255,255,0.15) transparent',
            marginRight: -8,
            paddingRight: 8,
          }}>
          {(reminders?.overdueTasks?.length || reminders?.dueSoonTasks?.length) ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(reminders?.overdueTasks ?? []).slice(0, 5).map((task) => (
                <div
                  key={task.id}
                  onClick={() => navigate('/tasks?highlightTask=' + task.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0', borderBottom: '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 99, fontWeight: 600, background: 'rgba(239,68,68,0.15)', color: '#ef4444', display: 'inline-block', whiteSpace: 'nowrap' }}>Overdue</span> {/* DS: #2 Alerts overdue badge */}
                  <span style={{ flex: 1, fontSize: '0.875rem' }}>{task.title}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {new Date(task.due_date).toLocaleDateString()}
                  </span>
                </div>
              ))}
              {(reminders?.dueSoonTasks ?? []).slice(0, 5).map((task) => (
                <div
                  key={task.id}
                  onClick={() => navigate('/tasks?highlightTask=' + task.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0', borderBottom: '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 99, fontWeight: 600, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', display: 'inline-block', whiteSpace: 'nowrap' }}>Due Soon</span> {/* DS: #3 Alerts compliance badge */}
                  <span style={{ flex: 1, fontSize: '0.875rem' }}>{task.title}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {new Date(task.due_date).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>No overdue tasks.</p>
          )}

          {/* Upcoming scheduled projects */}
          {((reminders?.overdueScheduledProjects?.length ?? 0) > 0 || (reminders?.scheduledProjects?.length ?? 0) > 0) && (
            <div style={{ marginTop: (reminders?.overdueTasks?.length || reminders?.dueSoonTasks?.length) ? 16 : 0 }}>
              <h4 style={{ marginBottom: 10, color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                UPCOMING PROJECTS ({(reminders?.overdueScheduledProjects?.length ?? 0) + (reminders?.scheduledProjects?.length ?? 0)})
              </h4>
              {(reminders?.overdueScheduledProjects ?? []).filter(p => !['completed','paid'].includes(p.workflow_status) && p.status !== 'cancelled').map((proj) => (
                <div key={`op-${proj.id}`} onClick={() => navigate('/projects')} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                }}>
                  <span className="badge badge-red">Overdue</span>
                  <span style={{ flex: 1, fontSize: '0.875rem' }}>{proj.name}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {formatDueDate(proj.scheduled_date)}
                  </span>
                </div>
              ))}
              {(reminders?.scheduledProjects ?? []).filter(p => !['completed','paid'].includes(p.workflow_status) && p.status !== 'cancelled').map((proj) => (
                <div key={`sp-${proj.id}`} onClick={() => navigate('/projects')} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                }}>
                  <span className="badge badge-blue">Scheduled</span>
                  <span style={{ flex: 1, fontSize: '0.875rem' }}>{proj.name}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {formatDueDate(proj.scheduled_date)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Scheduled compliance tasks */}
          {upcomingScheduled.length > 0 && (
            <div style={{ marginTop: reminders?.overdueTasks?.length ? 16 : 0 }}>
              <h4 style={{ marginBottom: 10, color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                COMPLIANCE TASKS ({upcomingScheduled.length})
              </h4>
              {upcomingScheduled.slice(0, 5).map((task: { id: number; name: string; nextOccurrenceDate: string; frequency: string; status: 'overdue' | 'due_soon' }) => (
                <div key={task.id} onClick={() => navigate('/tasks?tab=schedule&highlightTask=' + task.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0', borderBottom: '1px solid var(--border-subtle)',
                  cursor: 'pointer',
                }}>
                  <span className={task.status === 'overdue' ? 'badge badge-red' : 'badge badge-amber'}>
                    {task.status === 'overdue' ? 'Overdue' : 'Due Soon'}
                  </span>
                  <span style={{ flex: 1, fontSize: '0.875rem' }}>{task.name}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatDueDate(task.nextOccurrenceDate)}</span>
                </div>
              ))}
            </div>
          )}

          {reminders?.lowStockItems && reminders.lowStockItems.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ marginBottom: 10, color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                LOW STOCK ({reminders.lowStockItems.length} items)
              </h4>
              {reminders.lowStockItems.slice(0, 5).map((item) => (
                <div
                  key={item.id}
                  onClick={() => navigate('/inventory?tab=catalogue&itemId=' + item.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 0', borderBottom: '1px solid var(--border-subtle)',
                    fontSize: '0.875rem', cursor: 'pointer',
                  }}
                >
                  <span>{item.name}</span>
                  <span style={{ color: 'var(--accent-warning)' }}>
                    {item.quantity} / {item.reorder_point}
                  </span>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
        )}

        {/* Activity Feed */}
        {showRecentActivity && (
        <div className="dash-glass-card dashboard-activity" style={{ minWidth: 0 }}> {/* DS: #3 RecentActivity card wrapper */}
          <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}> {/* DS: #3 RecentActivity section title */}
            <Activity size={16} color="#3b82f6" className="card-title-icon" /> Recent Activity
          </h3>

          {activityLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
              <div className="loading-spinner" style={{ width: 24, height: 24 }} />
            </div>
          ) : activity.length === 0 ? (
            <div className="empty-state" style={{ padding: '32px 0' }}>
              <p>No recent activity yet</p>
            </div>
          ) : (
            <>
              <div style={{
                overflowY: 'auto',
                maxHeight: 244,
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--border-default) transparent',
                marginRight: -4,
                paddingRight: 4,
              }}>
                {activity.map((log) => (
                  <div
                    key={log.id}
                    style={{
                      display: 'flex', gap: 10, padding: '8px 0',
                      borderBottom: '1px solid var(--border-subtle)', alignItems: 'flex-start',
                      minHeight: 44,
                    }}
                  >
                    <span
                      className={`badge ${ACTIVITY_TYPE_COLORS[log.activity_type] ?? 'badge-gray'}`} // DS: #3 RecentActivity badge
                      style={{ marginTop: 2, flexShrink: 0 }}
                    >
                      {log.entity_type}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.description}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 2 }}> {/* DS: #3 RecentActivity timestamp */}
                        {log.username && `${log.username} · `}
                        {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {activity.length > 4 && (
                <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', paddingTop: 6, borderTop: '1px solid var(--border-subtle)' }}>
                  ↕ {activity.length - 4} more · scroll to view
                </div>
              )}
            </>
          )}
        </div>
        )}

      </div>

      {/* Bulletin Board — additive, appended after the existing dashboard-main-grid rather than
          inside it. Phase 1: schema + read-only render (mockups/dashboard-bulletin-board-hybrid.html).
          Doesn't touch the Scheduled Maintenance/Weather row, KPI grid, or the low-balance
          banner above. */}
      {showBulletinBoard && <BulletinBoard />}

      {/* Phase 3 — floating icon cluster (card customization cog + focus-mode zoom). Dashboard.tsx
          has no header row and is LOCKED, so this floats in a corner instead of a new header
          structure — same reasoning the Bulletin Board's settings cog used. Card customization
          (the cog) stays Pro+Enterprise gated per that feature's own decision; focus mode is a
          plain navigation/viewing feature, not premium content, so it's ungated and always
          rendered here — same as on every other page that now has this floating cluster. */}
      <div className="floating-action-cluster">
        {canCustomize && (
          <div ref={prefsPanelRef} style={{ position: 'relative' }}>
            {prefsPanelOpen && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 10px)', right: 0,
                width: 240,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-md)',
                padding: '10px 4px',
              }}>
                <div style={{ padding: '4px 12px 8px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>
                  Dashboard Cards
                </div>
                {DASHBOARD_CARDS.map((c) => {
                  const visible = !hiddenCards.includes(c.id);
                  return (
                    <label
                      key={c.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 9,
                        width: '100%', padding: '7px 12px',
                        cursor: 'pointer', fontSize: '0.8125rem',
                        color: visible ? 'var(--text-primary)' : 'var(--text-muted)',
                      }}
                    >
                      <input type="checkbox" checked={visible} onChange={() => toggleCard(c.id)} />
                      {c.label}
                    </label>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              className="combo-jello-spin"
              data-tint={prefsPanelOpen ? 'blue' : 'neutral'}
              onClick={() => setPrefsPanelOpen((o) => !o)}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = prefsPanelOpen ? '1' : '0.5'; spinIconOut(e); }}
              title="Customize dashboard cards"
              style={{
                width: 40, height: 40, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: prefsPanelOpen ? '1px solid rgba(59,130,246,0.4)' : '1px solid var(--border-default)',
                background: prefsPanelOpen ? 'rgba(59,130,246,0.16)' : 'var(--bg-elevated)',
                color: prefsPanelOpen ? '#60a5fa' : 'var(--text-secondary)',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-md)',
                opacity: prefsPanelOpen ? 1 : 0.5,
                transition: 'opacity 0.15s ease',
              }}
            >
              <Settings size={17} className="btn-icon" />
            </button>
          </div>
        )}
        <PageFocusToggle />
      </div>
    </div>
  );
}

// ─── Bulletin Board (Phase 1: schema + read-only render) ──────────────────────
// Ported from mockups/dashboard-bulletin-board-hybrid.html's hero+corkboard hybrid. That
// mockup is dark-only (hardcoded hexes, e.g. sticky-note gradients like
// linear-gradient(160deg,#26405f,#1f3450)), so category colors here are translucent tints over
// this app's own var(--*) surfaces instead — same rgba(color,0.12-0.15) treatment every other
// badge/tag/pill in this app already uses — rather than the mockup's literal opaque dark-navy
// gradients, which would look broken composited onto a light theme.
//
// Read-only this phase: reactions/ack/seen counts render as real numbers from the schema
// (announcement_reactions/announcement_reads via GET /api/announcements) but aren't clickable —
// no toggle handlers, no composer, no read-tracking writes. "New announcement" is present but
// inert (Phase 2 territory).

interface AnnouncementRow {
  id: number;
  title: string;
  body: string;
  category: 'urgent' | 'policy' | 'survey' | 'hr' | 'event' | 'kudos';
  is_pinned: boolean;
  pinned_at: string | null;
  requires_ack: boolean;
  requires_rsvp: boolean;
  event_date: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  created_at: string;
  updated_at: string;
  author_id: number | null;
  author_username: string | null;
  author_first_name: string | null;
  author_last_name: string | null;
  thumbs_up_count: number;
  ack_count: number;
  rsvp_yes_count: number;
  rsvp_no_count: number;
  celebrate_count: number;
  seen_count: number;
  is_read_by_me: boolean;
  my_reactions: string[];
}

const BB_CATEGORIES: { id: AnnouncementRow['category']; label: string; icon: string; color: string }[] = [
  { id: 'urgent', label: 'Urgent', icon: '🔴', color: '#ef4444' },
  { id: 'policy', label: 'Policy', icon: '📋', color: '#3b82f6' },
  { id: 'survey', label: 'Survey', icon: '🏛', color: '#f59e0b' },
  { id: 'hr',     label: 'HR',     icon: '👥', color: '#8b5cf6' },
  { id: 'event',  label: 'Events', icon: '🎉', color: '#10b981' },
  { id: 'kudos',  label: 'Kudos',  icon: '🎉', color: '#ec4899' },
];
const BB_CATEGORY_MAP: Record<string, typeof BB_CATEGORIES[number]> = Object.fromEntries(BB_CATEGORIES.map(c => [c.id, c]));

// Corkboard sticky-note backgrounds ONLY — mockups/dashboard-bulletin-board-hybrid.html's exact
// .sticky.{color} gradients (dark theme). Paired with BB_STICKY_GRADIENT_LIGHT below for light
// theme — BbStickyCard picks between the two via isLight. Scoped to BbStickyCard alone; nothing
// else in the app should use these.
const BB_STICKY_GRADIENT: Record<AnnouncementRow['category'], string> = {
  urgent: 'linear-gradient(160deg,#4a1f22,#3a181b)',
  policy: 'linear-gradient(160deg,#26405f,#1f3450)',
  survey: 'linear-gradient(160deg,#4a3a1c,#3a2d16)',
  hr: 'linear-gradient(160deg,#372a52,#2b2142)',
  event: 'linear-gradient(160deg,#1d3f33,#173328)',
  kudos: 'linear-gradient(160deg,#4a1f3a,#3a182e)',
};

// Light-theme counterpart to BB_STICKY_GRADIENT above — pastel Tailwind 100/200-tier tones per
// category, used when the app is in light theme so the corkboard cards stay readable/consistent
// instead of always rendering the dark-only mockup gradients.
const BB_STICKY_GRADIENT_LIGHT: Record<AnnouncementRow['category'], string> = {
  urgent: 'linear-gradient(160deg,#fee2e2,#fecaca)',
  policy: 'linear-gradient(160deg,#dbeafe,#bfdbfe)',
  survey: 'linear-gradient(160deg,#fef3c7,#fde68a)',
  hr: 'linear-gradient(160deg,#ede9fe,#ddd6fe)',
  event: 'linear-gradient(160deg,#d1fae5,#a7f3d0)',
  kudos: 'linear-gradient(160deg,#fce7f3,#fbcfe8)',
};

function bbAuthorName(a: AnnouncementRow): string {
  const full = `${a.author_first_name ?? ''} ${a.author_last_name ?? ''}`.trim();
  return full || a.author_username || 'Unknown';
}
function bbAuthorInitials(a: AnnouncementRow): string {
  const parts = bbAuthorName(a).split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return bbAuthorName(a).slice(0, 2).toUpperCase();
}

// Short-form relative time matching mockups/dashboard-bulletin-board-hybrid.html's .by/.rail-time
// convention ("2h ago", "4h", "1d") -- date-fns' own formatDistanceToNow has no short-unit mode
// (its shortest output is still verbose, e.g. "about 1 month"), so this app never actually
// produced the mockup's short form despite the otherwise-thorough "bring visuals in line with
// mockup" pass (commit 9e569a7) -- that commit fixed padding/pin/sticky-note styling but never
// touched the four formatDistanceToNow call sites this replaces. Deliberately coarse past 30
// days (mo/y) since the mockup itself never illustrates anything older than "3d".
function bbShortTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.max(0, Math.floor(diffMs / 60000));
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}

function BbTag({ category }: { category: AnnouncementRow['category'] }) {
  const c = BB_CATEGORY_MAP[category];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.62rem', fontWeight: 700,
      padding: '3px 9px', borderRadius: 99, letterSpacing: '0.03em', textTransform: 'uppercase',
      whiteSpace: 'nowrap', background: `${c.color}26`, color: c.color,
    }}>
      {c.icon} {c.label}
    </span>
  );
}

function BbReactPill({ children, onClick, active, onDark }: { children: React.ReactNode; onClick?: () => void; active?: boolean; onDark?: boolean }) {
  // Toggle chip, not a full icon-wiggle button — a subtle lift + brighten on hover (same
  // brightness-filter technique KpiCard already uses), plus a highlighted "active" state when
  // the current user has this reaction on the announcement.
  return (
    <span
      // stopPropagation so reacting on a corkboard card's pill doesn't also trigger the card's
      // own onClick (swap-into-hero) — harmless no-op for the hero's own pills, which have no
      // click-catching ancestor.
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{
        // Matches mockup's .react / .react.on exactly (rgba(255,255,255,0.05) resting,
        // rgba(59,130,246,0.4)/0.1 border/background when active). onDark matches the mockup's
        // .s-foot .react override (rgba(255,255,255,.1)/.14/.75) — used only on the corkboard's
        // now-solid-gradient cards, where the theme-reactive var(--text-secondary)/border-subtle
        // resting colors below would be unreadable in light theme against an always-dark card.
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 99,
        border: active ? '1px solid rgba(59,130,246,0.4)' : onDark ? '1px solid rgba(255,255,255,0.14)' : '1px solid var(--border-subtle)',
        background: active ? 'rgba(59,130,246,0.1)' : onDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)', fontSize: '0.72rem',
        color: active ? '#93c5fd' : onDark ? 'rgba(255,255,255,0.75)' : 'var(--text-secondary)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.12s ease, filter 0.12s ease',
      }}
      onMouseEnter={onClick ? (e) => { (e.currentTarget as HTMLElement).style.filter = 'brightness(1.18)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; } : undefined}
      onMouseLeave={onClick ? (e) => { (e.currentTarget as HTMLElement).style.filter = ''; (e.currentTarget as HTMLElement).style.transform = ''; } : undefined}
    >
      {children}
    </span>
  );
}

type BbReactionType = 'thumbs_up' | 'ack' | 'rsvp_yes' | 'rsvp_no' | 'celebrate';

function bbReactionTypeFor(a: AnnouncementRow): BbReactionType {
  if (a.requires_ack) return 'ack';
  if (a.requires_rsvp) return 'rsvp_yes';
  if (a.category === 'kudos') return 'celebrate';
  return 'thumbs_up';
}

function BulletinBoard() {
  const [heroIndex, setHeroIndex] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState<AnnouncementRow['category'] | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerEditing, setComposerEditing] = useState<AnnouncementRow | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'org_admin';
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.get<{ announcements: AnnouncementRow[]; eligibleUserCount: number }>('/announcements'),
  });
  const announcements: AnnouncementRow[] = (data as any)?.data?.announcements ?? [];
  const eligibleUserCount: number = (data as any)?.data?.eligibleUserCount ?? 0;

  // Same queryKey Sidebar.tsx uses for its facility switcher — shares that cache entry instead
  // of firing a second request. Mockup subtitle format: "All 16 facilities · 6 active · 3 unread".
  const { data: facilitiesRes } = useQuery({
    queryKey: ['facilities-list'],
    queryFn: () => api.get<Facility[]>('/facilities'),
    staleTime: 5 * 60_000,
  });
  const facilityCount: number = (facilitiesRes as any)?.data?.length ?? 0;

  // Hero rotates through pinned items only, matching the mockup ("Hero rotates through the
  // pinned announcements"); falls back to the single most recent announcement if nothing is
  // pinned yet, so the board still shows something meaningful for orgs with no pins.
  const pinned = announcements.filter(a => a.is_pinned);
  const heroList = pinned.length > 0 ? pinned : announcements.slice(0, 1);
  const safeHeroIndex = Math.min(heroIndex, heroList.length - 1);
  const hero: AnnouncementRow | undefined = heroList[safeHeroIndex];

  const reactMutation = useMutation({
    mutationFn: ({ id, reaction_type }: { id: number; reaction_type: BbReactionType }) =>
      api.post(`/announcements/${id}/reactions/toggle`, { reaction_type }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });

  const readMutation = useMutation({
    mutationFn: (id: number) => api.post(`/announcements/${id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });

  // Pin/unpin is a quick action, not a full composer round-trip — but the PATCH route
  // overwrites title/body/category unconditionally from whatever's in the request body, so a
  // pin-only submit has to resend the announcement's existing fields (client already has them
  // in `a`) rather than just `is_pinned` alone.
  const pinMutation = useMutation({
    mutationFn: (a: AnnouncementRow) => {
      const fd = new FormData();
      fd.append('title', a.title);
      fd.append('body', a.body);
      fd.append('category', a.category);
      fd.append('is_pinned', String(!a.is_pinned));
      fd.append('requires_ack', String(a.requires_ack));
      fd.append('requires_rsvp', String(a.requires_rsvp));
      if (a.event_date) fd.append('event_date', a.event_date);
      return api.patchForm(`/announcements/${a.id}`, fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/announcements/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['announcements'] }); setConfirmDeleteId(null); },
  });

  // Hero display = "viewed" (passive-view semantics per the mockup's "👀 seen" count).
  useEffect(() => {
    if (hero && !hero.is_read_by_me) readMutation.mutate(hero.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hero?.id]);

  function openComposer(a: AnnouncementRow | null) {
    setComposerEditing(a);
    setComposerOpen(true);
  }
  function closeComposer() {
    setComposerOpen(false);
    setComposerEditing(null);
  }

  if (isLoading) {
    return (
      <div className="dash-glass-card" style={{ marginTop: 20, padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading bulletin board…
      </div>
    );
  }
  if (announcements.length === 0 && !isAdmin) return null; // nothing to show, no way to add it either
  if (announcements.length === 0) {
    // Admin with no announcements yet — still show a minimal header so the composer is reachable.
    return (
      <div className="dash-glass-card" style={{ marginTop: 20, padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Megaphone size={18} color="#f472b6" className="card-title-icon" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--text-primary)' }}>Bulletin Board</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>No announcements yet</div>
          </div>
          <button className="btn btn-primary combo2" onClick={() => openComposer(null)} style={{ fontSize: '0.82rem' }}>
            <Megaphone size={14} className="btn-icon" /> New announcement
          </button>
        </div>
        {composerOpen && <AnnouncementComposer editing={composerEditing} onClose={closeComposer} />}
      </div>
    );
  }

  const heroCat = BB_CATEGORY_MAP[hero.category];

  // Rail lists the hero itself first (marked selected, matching the mockup) plus other
  // pinned/recent items — everything except whichever announcement is currently in the hero.
  const railItems = announcements.filter(a => a.id !== hero.id).slice(0, 5);

  const unreadCount = announcements.filter(a => !a.is_read_by_me).length;
  const filtered = categoryFilter ? announcements.filter(a => a.category === categoryFilter) : announcements;

  return (
    <div className="dash-glass-card" style={{ marginTop: 20, padding: 0 }}>
      {/* padding:0 above is required — .dash-glass-card defaults to padding:20px, which was
          stacking on top of .bb-head/.hero-main/.cork-wrap's own padding below and made the whole
          card noticeably taller/larger than the mockup (whose own .card class has no padding at
          all — every section supplies its own). */}
      {/* .bb-hero-grid needs a media query (1.15fr 320px -> single column under 1000px, matching
          the mockup exactly), which inline style can't express. Self-contained <style> tag here
          instead of adding a new buttonAnimations.css import to this locked file — same inline-
          style-tag technique RichTextEditor.tsx already uses for its placeholder pseudo-element. */}
      <style>{`
        .bb-hero-grid { display: grid; grid-template-columns: 1.15fr 320px; }
        @media (max-width: 1000px) {
          .bb-hero-grid { grid-template-columns: 1fr; }
          .bb-hero-rail { border-left: none !important; border-top: 1px solid var(--border-subtle); }
        }
        /* Corkboard scatter — matches the mockup's .sticky exactly: per-card resting rotation via
           nth-child, straightens/lifts/scales on hover. Inline styles can't express :nth-child or
           :hover, hence this scoped <style> tag (same technique as .bb-hero-grid above). */
        .bb-cork-grid > .bb-sticky {
          box-shadow: 0 8px 20px rgba(0,0,0,0.35);
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .bb-cork-grid > .bb-sticky:hover {
          transform: translateY(-4px) rotate(0deg) scale(1.015) !important;
          box-shadow: 0 14px 32px rgba(0,0,0,0.5) !important;
          z-index: 3;
        }
        .bb-cork-grid > .bb-sticky:nth-child(4n) { transform: rotate(-1.5deg); }
        .bb-cork-grid > .bb-sticky:nth-child(4n+1) { transform: rotate(1.2deg); }
        .bb-cork-grid > .bb-sticky:nth-child(4n+2) { transform: rotate(-0.6deg); }
        .bb-cork-grid > .bb-sticky:nth-child(4n+3) { transform: rotate(0.8deg); }
        /* Decorative amber glow behind the hero — mockup's .hero-main::before exactly. A ::before
           pseudo-element can't be expressed via inline style, hence this scoped <style> tag. */
        .bb-hero-main::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(500px 200px at 15% 0%, rgba(245,158,11,0.10), transparent 70%);
          pointer-events: none;
        }
      `}</style>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
        <Megaphone size={18} color="#f472b6" className="card-title-icon" style={{ flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--text-primary)' }}>Bulletin Board</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
            {facilityCount > 0 && `All ${facilityCount} ${facilityCount === 1 ? 'facility' : 'facilities'} · `}{announcements.length} active · {unreadCount} unread
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-ghost" style={{ fontSize: '0.82rem' }} onMouseLeave={spinIconOut}><span className="btn-icon">🔔</span> {unreadCount} unread</button>
          {isAdmin && (
            <button className="btn btn-primary combo2" onClick={() => openComposer(null)} style={{ fontSize: '0.82rem' }}>
              <Megaphone size={14} className="btn-icon" /> New announcement
            </button>
          )}
        </div>
      </div>

      {/* Hero */}
      <div className="bb-hero-grid" style={{ display: 'grid', borderBottom: '1px solid var(--border-subtle)' }}>
        <div className="bb-hero-main" style={{ padding: 24, position: 'relative', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap', position: 'relative' }}>
            <BbTag category={hero.category} />
            {hero.is_pinned && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.64rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#fbbf24' }}>
                📌 Featured
              </span>
            )}
            {isAdmin && (
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* No inline background here — combo-jello-spin's own CSS supplies transparent-at-
                    rest / tinted-on-hover via [data-tint]:hover; an inline background (even
                    'none') always beats an external CSS rule regardless of specificity and would
                    silently block the hover tint, same trap flagged elsewhere this session. */}
                <button className="combo-jello-spin" data-tint="neutral" onClick={() => openComposer(hero)} title="Edit" style={{ border: 'none', cursor: 'pointer', padding: 6, borderRadius: 7, display: 'flex' }}>
                  <Pencil size={13} className="btn-icon" style={{ color: 'var(--text-muted)' }} />
                </button>
                <button className="combo-jello-spin" data-tint={hero.is_pinned ? 'amber' : 'neutral'} onClick={() => pinMutation.mutate(hero)} title={hero.is_pinned ? 'Unpin' : 'Pin'} style={{ border: 'none', cursor: 'pointer', padding: 6, borderRadius: 7, display: 'flex' }}>
                  <Pin size={13} className="btn-icon" style={{ color: hero.is_pinned ? '#fbbf24' : 'var(--text-muted)' }} />
                </button>
                <button className="combo-jello-spin" data-tint="red" onClick={() => setConfirmDeleteId(hero.id)} title="Delete" style={{ border: 'none', cursor: 'pointer', padding: 6, borderRadius: 7, display: 'flex' }}>
                  <Trash2 size={13} className="btn-icon" style={{ color: 'var(--text-muted)' }} />
                </button>
              </div>
            )}
          </div>
          <div style={{ fontSize: '1.55rem', fontWeight: 700, lineHeight: 1.18, letterSpacing: '-0.01em', color: 'var(--text-primary)', position: 'relative' }}>
            {hero.title}
          </div>
          <div style={{ fontSize: '0.86rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginTop: 12, position: 'relative' }}>
            {hero.body}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, flexWrap: 'wrap', position: 'relative' }}>
            {hero.attachment_name && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'rgba(255,255,255,0.03)', fontSize: '0.72rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                <span style={{ color: '#f87171', fontWeight: 700, fontSize: '0.6rem' }}>PDF</span> {hero.attachment_name}
              </span>
            )}
            <BbReactPill active={hero.my_reactions.includes('thumbs_up')} onClick={() => reactMutation.mutate({ id: hero.id, reaction_type: 'thumbs_up' })}>
              👍 {hero.thumbs_up_count}
            </BbReactPill>
            {hero.requires_ack && (
              <BbReactPill active={hero.my_reactions.includes('ack')} onClick={() => reactMutation.mutate({ id: hero.id, reaction_type: 'ack' })}>
                ✅ {hero.ack_count}/{eligibleUserCount}
              </BbReactPill>
            )}
            {hero.requires_rsvp && (
              <BbReactPill active={hero.my_reactions.includes('rsvp_yes')} onClick={() => reactMutation.mutate({ id: hero.id, reaction_type: 'rsvp_yes' })}>
                📅 RSVP {hero.rsvp_yes_count}
              </BbReactPill>
            )}
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
              {bbAuthorName(hero)} · {bbShortTime(new Date(hero.created_at))} ago
            </span>
          </div>
          {heroList.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, position: 'relative' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {heroList.map((_, i) => (
                  <span
                    key={i}
                    onClick={() => setHeroIndex(i)}
                    style={{
                      width: i === safeHeroIndex ? 22 : 7, height: 7, borderRadius: 99, cursor: 'pointer',
                      background: i === safeHeroIndex ? heroCat.color : 'var(--border-strong)',
                      transition: 'all 0.2s',
                    }}
                  />
                ))}
              </div>
              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
                {safeHeroIndex + 1} / {heroList.length} pinned
              </span>
            </div>
          )}
        </div>
        <div className="bb-hero-rail" style={{ borderLeft: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 18px 8px', fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
            📌 More pinned &amp; latest
          </div>
          <div
            onClick={() => setHeroIndex(safeHeroIndex)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 18px', borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', background: `${heroCat.color}0f`, boxShadow: `inset 2px 0 0 ${heroCat.color}` }}
          >
            <span style={{ width: 8, height: 8, borderRadius: 99, background: heroCat.color, flexShrink: 0, marginTop: 4 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.3, color: 'var(--text-primary)' }}>{hero.title}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2 }}>{bbAuthorName(hero)}</div>
            </div>
            <span style={{ fontSize: '0.64rem', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
              {bbShortTime(new Date(hero.created_at))}
            </span>
          </div>
          {railItems.map(a => {
            const idxInHeroList = heroList.findIndex(h => h.id === a.id);
            const cat = BB_CATEGORY_MAP[a.category];
            return (
              <div
                key={a.id}
                onClick={() => { if (idxInHeroList !== -1) setHeroIndex(idxInHeroList); }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 18px', borderTop: '1px solid var(--border-subtle)', cursor: idxInHeroList !== -1 ? 'pointer' : 'default', transition: 'background 0.15s' }}
              >
                {a.is_read_by_me ? (
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: cat.color, flexShrink: 0, marginTop: 4 }} />
                ) : (
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: 'var(--accent-primary)', boxShadow: '0 0 0 3px rgba(59,130,246,0.2)', flexShrink: 0, marginTop: 4 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.3, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <BbTag category={a.category} />
                  </div>
                </div>
                <span style={{ fontSize: '0.64rem', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
                  {bbShortTime(new Date(a.created_at))}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Corkboard */}
      <div style={{
        padding: '18px 20px 22px',
        background: 'radial-gradient(circle at 15% 20%, rgba(120,90,60,0.08), transparent 45%), radial-gradient(circle at 85% 80%, rgba(120,90,60,0.06), transparent 45%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>On the board</span>
          <div style={{ flex: 1 }} />
          <button
            className={`category-pill${categoryFilter === null ? ' active' : ''}`}
            onClick={() => setCategoryFilter(null)}
            style={{
              fontSize: '0.72rem', fontWeight: 500, padding: '5px 12px', borderRadius: 99, cursor: 'pointer',
              border: categoryFilter === null ? '1px solid rgba(59,130,246,0.4)' : '1px solid var(--border-subtle)',
              background: categoryFilter === null ? 'rgba(59,130,246,0.14)' : 'transparent',
              color: categoryFilter === null ? '#93c5fd' : 'var(--text-secondary)',
            }}
          >
            All
          </button>
          {BB_CATEGORIES.map(c => {
            const active = categoryFilter === c.id;
            return (
              <button
                key={c.id}
                className={`category-pill${active ? ' active' : ''}`}
                onClick={() => setCategoryFilter(c.id)}
                style={{
                  fontSize: '0.72rem', fontWeight: 500, padding: '5px 12px', borderRadius: 99, cursor: 'pointer',
                  border: active ? `1px solid ${c.color}66` : '1px solid var(--border-subtle)',
                  background: active ? `${c.color}22` : 'transparent',
                  color: active ? c.color : 'var(--text-secondary)',
                }}
              >
                {c.icon} {c.label}
              </button>
            );
          })}
        </div>
        <div className="bb-cork-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(255px, 1fr))', gap: 18 }}>
          {filtered.map(a => {
            // Same constraint the rail already has: the hero can only ever display an item from
            // heroList (pinned announcements, or the single-fallback when nothing's pinned) — a
            // card for a non-pinned announcement (when other items ARE pinned) has no hero slot
            // to swap into, so it gets no click-to-feature handler, matching the rail's existing
            // idxInHeroList !== -1 guard exactly.
            const idxInHeroList = heroList.findIndex(h => h.id === a.id);
            return (
              <BbStickyCard
                key={a.id}
                a={a}
                eligibleUserCount={eligibleUserCount}
                isAdmin={isAdmin}
                onReact={(id, reaction_type) => reactMutation.mutate({ id, reaction_type })}
                onSeen={(id) => readMutation.mutate(id)}
                onEdit={openComposer}
                onPinToggle={(row) => pinMutation.mutate(row)}
                onDeleteRequest={setConfirmDeleteId}
                onSelectHero={idxInHeroList !== -1 ? () => setHeroIndex(idxInHeroList) : undefined}
              />
            );
          })}
        </div>
      </div>

      {composerOpen && <AnnouncementComposer editing={composerEditing} onClose={closeComposer} />}

      {confirmDeleteId !== null && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmDeleteId(null); }}
        >
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Announcement</h3>
              <button className="modal-close" onClick={() => setConfirmDeleteId(null)}>
                <X size={18} className="btn-icon" />
              </button>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Delete this announcement? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost combo-cancel-jello" onClick={() => setConfirmDeleteId(null)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => deleteMutation.mutate(confirmDeleteId)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

interface BbStickyCardProps {
  a: AnnouncementRow;
  eligibleUserCount: number;
  isAdmin: boolean;
  onReact: (id: number, reaction_type: BbReactionType) => void;
  onSeen: (id: number) => void;
  onEdit: (a: AnnouncementRow) => void;
  onPinToggle: (a: AnnouncementRow) => void;
  onDeleteRequest: (id: number) => void;
  onSelectHero?: () => void;
}

function BbStickyCard({ a, eligibleUserCount, isAdmin, onReact, onSeen, onEdit, onPinToggle, onDeleteRequest, onSelectHero }: BbStickyCardProps) {
  const cat = BB_CATEGORY_MAP[a.category];
  const reactionType = bbReactionTypeFor(a);
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === 'light';

  // Corkboard card rendering = "seen" (every card is on-screen at once, no scroll-based
  // visibility tracking in this app) — same passive-view semantics as the hero's read-on-display.
  useEffect(() => {
    if (!a.is_read_by_me) onSeen(a.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.id]);

  return (
    <div
      className="bb-sticky"
      onClick={onSelectHero}
      style={{
        // Solid per-category gradient (mockups/dashboard-bulletin-board-hybrid.html's .sticky.
        // {color} exactly) — was a translucent ${cat.color}1a tint over the page background,
        // which read as glassy/washed-out next to the mockup's opaque sticky-note look. No border
        // (mockup's .sticky has none either — box-shadow alone defines the edge, via .bb-sticky
        // above). box-shadow/rotation/hover also come from .bb-sticky so :nth-child/:hover apply.
        position: 'relative', borderRadius: 5, padding: '16px 16px 14px', minHeight: 150,
        display: 'flex', flexDirection: 'column',
        background: isLight ? BB_STICKY_GRADIENT_LIGHT[a.category] : BB_STICKY_GRADIENT[a.category],
        cursor: 'pointer',
      }}
    >
      <span style={{
        // Matches mockup's .pinpop exactly: 16x16 (was 14x14), inset shading for the 3D pushpin
        // look (was missing), z-index so it stays above the card's own content.
        position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)',
        width: 16, height: 16, borderRadius: 99, background: cat.color,
        boxShadow: '0 3px 5px rgba(0,0,0,0.4), inset -2px -2px 3px rgba(0,0,0,0.25)',
        zIndex: 1,
      }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
        <BbTag category={a.category} />
        {isAdmin && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* No inline background — see the matching comment on the hero's edit/pin/delete
                buttons above; combo-jello-spin's own CSS handles rest/hover background. */}
            <button className="combo-jello-spin" data-tint="neutral" onClick={(e) => { e.stopPropagation(); onEdit(a); }} title="Edit" style={{ border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex' }}>
              <Pencil size={11} className="btn-icon" style={{ color: 'var(--text-muted)' }} />
            </button>
            <button className="combo-jello-spin" data-tint={a.is_pinned ? 'amber' : 'neutral'} onClick={(e) => { e.stopPropagation(); onPinToggle(a); }} title={a.is_pinned ? 'Unpin' : 'Pin'} style={{ border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex' }}>
              <Pin size={11} className="btn-icon" style={{ color: a.is_pinned ? '#fbbf24' : 'var(--text-muted)' }} />
            </button>
            <button className="combo-jello-spin" data-tint="red" onClick={(e) => { e.stopPropagation(); onDeleteRequest(a.id); }} title="Delete" style={{ border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex' }}>
              <Trash2 size={11} className="btn-icon" style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>
        )}
      </div>
      {/* Title/body/attachment/footer below use isLight-branched literal colors rather than
          var(--text-*) — the card background above is a literal per-category gradient (dark or
          light per isLight), and var(--text-*) wouldn't track that gradient's own contrast
          needs the way these hand-picked light/dark pairs do. */}
      <div style={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.3, color: isLight ? '#1e293b' : '#f9fafb' }}>{a.title}</div>
      <div style={{ fontSize: '0.76rem', color: isLight ? 'rgba(15,23,42,0.68)' : 'rgba(255,255,255,0.74)', lineHeight: 1.5, marginTop: 6, flex: 1 }}>{a.body}</div>
      {a.attachment_name && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 9, fontSize: '0.66rem', color: isLight ? 'rgba(15,23,42,0.55)' : 'rgba(255,255,255,0.6)', padding: '4px 8px', borderRadius: 6, background: isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.08)', width: 'max-content' }}>
          <span style={{ color: isLight ? '#dc2626' : '#fca5a5', fontWeight: 700, fontSize: '0.58rem' }}>PDF</span> {a.attachment_name}
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: '0.66rem', color: isLight ? 'rgba(15,23,42,0.5)' : 'rgba(255,255,255,0.55)' }}>
        <span style={{ width: 20, height: 20, borderRadius: 99, fontSize: '0.56rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0d1526', background: cat.color, flexShrink: 0 }}>
          {bbAuthorInitials(a)}
        </span>
        <span>{bbAuthorName(a)}</span>
        <span>·</span>
        <span>{bbShortTime(new Date(a.created_at))}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.62rem' }}>👀 {a.seen_count}</span>
          <BbReactPill onDark={!isLight} active={a.my_reactions.includes(reactionType)} onClick={() => onReact(a.id, reactionType)}>
            {reactionType === 'ack' ? `✅ ${a.ack_count}/${eligibleUserCount}`
              : reactionType === 'rsvp_yes' ? `🙋 ${a.rsvp_yes_count}`
              : reactionType === 'celebrate' ? `🎉 ${a.celebrate_count}`
              : `👍 ${a.thumbs_up_count}`}
          </BbReactPill>
        </span>
      </div>
    </div>
  );
}

// ─── Announcement Composer (Phase 2) ───────────────────────────────────────────
// Same modal shell as every other add/edit modal in this app (createPortal, fixed
// header/scrollable-body/fixed-footer, modal-icon-intro title icon) — see Vendors.tsx's
// Add/Edit Vendor modal for the reference pattern this mirrors.

function AnnouncementComposer({ editing, onClose }: { editing: AnnouncementRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const mouseDownTarget = useRef<EventTarget | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState(editing?.title ?? '');
  const [category, setCategory] = useState<AnnouncementRow['category']>(editing?.category ?? 'policy');
  const [bodyMd, setBodyMd] = useState(editing?.body ?? '');
  const [isPinned, setIsPinned] = useState(editing?.is_pinned ?? false);
  const [requiresAck, setRequiresAck] = useState(editing?.requires_ack ?? false);
  const [requiresRsvp, setRequiresRsvp] = useState(editing?.requires_rsvp ?? false);
  // Date picker inputs need YYYY-MM-DD only — API returns a full ISO string.
  const [eventDate, setEventDate] = useState(editing?.event_date ? editing.event_date.slice(0, 10) : '');
  const [file, setFile] = useState<File | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('body', bodyMd.trim());
      fd.append('category', category);
      fd.append('is_pinned', String(isPinned));
      fd.append('requires_ack', String(requiresAck));
      fd.append('requires_rsvp', String(requiresRsvp));
      if (category === 'event' && eventDate) fd.append('event_date', eventDate);
      if (file) fd.append('attachment', file);
      return editing ? api.patchForm(`/announcements/${editing.id}`, fd) : api.postForm('/announcements', fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['announcements'] });
      onClose();
    },
    onError: (err: any) => {
      // Was previously unhandled — a failed save (role/tier check, validation, DB error) had no
      // way to reach the user; the modal just sat there looking like nothing happened.
      setSubmitError(err?.response?.data?.error ?? 'Failed to save announcement. Please try again.');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !bodyMd.trim()) {
      // Previously the submit button was just `disabled` in this case with no explanation —
      // clicking it (or trying to) produced literally no feedback. Now it's always clickable and
      // an actual attempt surfaces why it didn't go through.
      setSubmitError('Title and body are both required.');
      return;
    }
    setSubmitError(null);
    mutation.mutate();
  }

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" style={{ maxWidth: 620, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
        {/* Fixed Header */}
        <div style={{ flexShrink: 0, padding: '24px 28px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="modal-header" style={{ marginBottom: 0 }}>
            <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Megaphone size={18} className="modal-icon-intro" style={{ color: '#f472b6' }} />
              {editing ? 'Edit Announcement' : 'New Announcement'}
            </h3>
            <button className="modal-close" onClick={onClose}>
              <X size={18} className="btn-icon" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, padding: '20px 28px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

              {submitError && (
                <div style={{
                  fontSize: '0.8rem', color: '#f87171', padding: '8px 12px',
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                  borderRadius: 7,
                }}>
                  {submitError}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Title *</label>
                <input
                  className="form-input"
                  required
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); if (submitError) setSubmitError(null); }}
                  placeholder="e.g. Fire drill scheduled for Thursday"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Category</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {BB_CATEGORIES.map(c => {
                    const active = category === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={`category-pill${active ? ' active' : ''}`}
                        onClick={() => setCategory(c.id)}
                        style={{
                          fontSize: '0.78rem', fontWeight: 500, padding: '6px 13px', borderRadius: 99, cursor: 'pointer',
                          border: active ? `1px solid ${c.color}66` : '1px solid var(--border-subtle)',
                          background: active ? `${c.color}22` : 'transparent',
                          color: active ? c.color : 'var(--text-secondary)',
                        }}
                      >
                        {c.icon} {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {category === 'event' && (
                <div className="form-group">
                  <label className="form-label">Event Date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Body *</label>
                <RichTextEditor
                  value={bodyMd}
                  onChange={(md) => { setBodyMd(md); if (submitError) setSubmitError(null); }}
                  placeholder="Write your announcement…"
                  rows={6}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Attachment</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="*/*"
                  style={{ display: 'none' }}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-sm combo-jello-spin"
                  data-tint="neutral"
                  onClick={() => fileInputRef.current?.click()}
                  onMouseLeave={spinIconOut}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto' }}
                >
                  <Paperclip size={13} className="btn-icon" />
                  {file ? file.name : editing?.attachment_name ? `Replace "${editing.attachment_name}"` : 'Attach file'}
                </button>
              </div>

              <hr className="divider" style={{ margin: '0' }} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />
                  📌 Pin to top of board
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} />
                  ✅ Requires acknowledgement
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={requiresRsvp} onChange={(e) => setRequiresRsvp(e.target.checked)} />
                  🙋 Requires RSVP
                </label>
              </div>

            </div>
          </div>
          {/* Fixed Footer */}
          <div style={{ flexShrink: 0, padding: '16px 28px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary combo2" disabled={mutation.isPending}>
              {mutation.isPending ? 'Posting…' : editing ? 'Save changes' : 'Post announcement'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
